import { useEffect, useState } from "react";
import { TextField, Dialog, DialogActions, DialogContent, DialogTitle, Button, Alert, Grid, Divider, Chip, Autocomplete, MenuItem } from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Countries } from "../../../utils/Lookup";
import { GroupHeader, GroupItems } from "../../../utils/Styled";
import { useAuth } from "../../../hooks/useAuth";
import { GetTokenIdentity } from "../../../utils/Token";

import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";

const REMINDER_MONTH_OPTIONS = [1, 2, 3, 6, 9, 12, 15, 18];
const CUSTOM_SCHEDULE_VALUE = "custom";

const createDefaultReminder = () => ({
    schedule_month: 1,
    reminder_date: moment().add(1, "months").toDate(),
});

export default function CreateReminderForm({ open, handleClose, rowData, fetchData }) {
    const { token } = useAuth();
    const identity = GetTokenIdentity(token);

    const [newData, setNewData] = useState({
        org_id: "",
        org_name: "",
        email: "",
        office_phone: "",
        mobile_phone: "",
        pic_name: "",
        product_id: "",
        is_assigned_to_group: 0,
        assigned_to: "",
        remark: "",
    });
    const [reminderRows, setReminderRows] = useState([]);

    const [errors, setErrors] = useState({ assigned_to: false, remark: false, reminders: false });
    const [helperText, setHelperText] = useState({});
    const [reminderErrors, setReminderErrors] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");

    const [productsData, setProductsData] = useState([]);
    const [usersData, setUsersData] = useState([]);
    const [selectedProductValue, setSelectedProductValue] = useState(null);
    const [selectedUserValue, setSelectedUserValue] = useState(null);

    useEffect(() => {
        if (open) {
            setNewData({
                org_id: rowData ? rowData.org_id : "",
                org_name: rowData ? rowData.org_name || "" : "",
                email: rowData ? rowData.email || "" : "",
                office_phone: rowData ? rowData.office_phone || "" : "",
                mobile_phone: rowData ? rowData.mobile_phone || "" : "",
                pic_name: rowData ? rowData.pic_name || "" : "",
                product_id: "",
                is_assigned_to_group: 0,
                assigned_to: "",
                remark: "",
            });
            setReminderRows([]);
            setSelectedProductValue(null);
            setSelectedUserValue(null);
            setErrors({ assigned_to: false, remark: false, reminders: false });
            setHelperText({});
            setReminderErrors([]);
            setErrorMessage("");

            axios
                .get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/products/select")
                .then((response) => {
                    setProductsData(response.data.data.products);
                })
                .catch((error) => console.log(error));

            axios
                .get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/users/groups/select")
                .then((response) => {
                    const users = response.data.data.users;
                    setUsersData(users);

                    const currentUserOption = users.find((option) => Number(option.is_group) === 0 && Number(option.id) === Number(identity.user.user_id));
                    setSelectedUserValue(currentUserOption || null);
                    setNewData((prevData) => ({
                        ...prevData,
                        assigned_to: currentUserOption ? currentUserOption.id : "",
                        is_assigned_to_group: currentUserOption ? 0 : prevData.is_assigned_to_group,
                    }));
                })
                .catch((error) => console.log(error));
        }
    }, [open, rowData, identity.user.user_id]);

    const setError = async (field, message) => {
        await setErrors((prevErrors) => ({ ...prevErrors, [field]: true }));
        await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: message }));
    };

    const validate = async (field, value) => {
        if (field === "assigned_to" && value === "") {
            await setError("assigned_to", "Assigned To is required.");
        } else if (field === "remark" && (value || "").trim() === "") {
            await setError("remark", "Remark is required.");
        } else {
            await setErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
            await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: "" }));
        }
    };

    const handleAddReminder = () => {
        setReminderRows((prevRows) => [...prevRows, createDefaultReminder()]);
        setReminderErrors((prevErrors) => [...prevErrors, false]);
    };

    const handleRemoveReminder = (index) => {
        setReminderRows((prevRows) => prevRows.filter((_, rowIndex) => rowIndex !== index));
        setReminderErrors((prevErrors) => prevErrors.filter((_, rowIndex) => rowIndex !== index));
    };

    const handleReminderMonthChange = (index, monthValue) => {
        setReminderRows((prevRows) => {
            const nextRows = [...prevRows];

            if (monthValue === CUSTOM_SCHEDULE_VALUE) {
                nextRows[index] = {
                    ...nextRows[index],
                    schedule_month: CUSTOM_SCHEDULE_VALUE,
                };
                return nextRows;
            }

            const parsedMonthValue = Number(monthValue);
            if (!Number.isFinite(parsedMonthValue)) {
                return nextRows;
            }

            nextRows[index] = {
                ...nextRows[index],
                schedule_month: parsedMonthValue,
                reminder_date: moment().add(parsedMonthValue, "months").toDate(),
            };
            return nextRows;
        });

        setReminderErrors((prevErrors) => {
            const nextErrors = [...prevErrors];
            nextErrors[index] = false;
            return nextErrors;
        });
    };

    const handleReminderDateChange = (index, dateValue) => {
        setReminderRows((prevRows) => {
            const nextRows = [...prevRows];
            nextRows[index] = { ...nextRows[index], reminder_date: dateValue, schedule_month: CUSTOM_SCHEDULE_VALUE };
            return nextRows;
        });

        setReminderErrors((prevErrors) => {
            const nextErrors = [...prevErrors];
            nextErrors[index] = !moment(dateValue).isValid();
            return nextErrors;
        });
    };

    const validateReminderRows = () => {
        if (reminderRows.length === 0) {
            setErrors((prevErrors) => ({ ...prevErrors, reminders: true }));
            setHelperText((prevHelperText) => ({ ...prevHelperText, reminders: "At least one reminder is required." }));
            setReminderErrors([]);
            return false;
        }

        const nextReminderErrors = reminderRows.map((row) => !moment(row.reminder_date).isValid());
        const hasRowError = nextReminderErrors.some(Boolean);

        setReminderErrors(nextReminderErrors);
        setErrors((prevErrors) => ({ ...prevErrors, reminders: hasRowError }));
        setHelperText((prevHelperText) => ({
            ...prevHelperText,
            reminders: hasRowError ? "Please set a valid date for every reminder row." : "",
        }));

        return !hasRowError;
    };

    const handleCreate = async () => {
        let isValid = true;

        if (!newData.assigned_to) isValid = false;
        if (!newData.remark || newData.remark.trim() === "") isValid = false;

        await validate("assigned_to", newData.assigned_to);
        await validate("remark", newData.remark);

        const areReminderRowsValid = validateReminderRows();
        if (!areReminderRowsValid) {
            isValid = false;
        }

        if (!isValid) {
            setErrorMessage("Please fill all the required fields.");
            return;
        }

        try {
            setErrorMessage("");

            for (let index = 0; index < reminderRows.length; index++) {
                const row = reminderRows[index];
                const reminderDate = moment(row.reminder_date).format("YYYY-MM-DD");

                await axios.post(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/touchpoints/create", {
                    org_id: newData.org_id,
                    product_id: newData.product_id ? newData.product_id : "",
                    contact_date: reminderDate,
                    reminder_date: reminderDate,
                    is_assigned_to_group: newData.is_assigned_to_group,
                    assigned_to: newData.assigned_to,
                    remark: newData.remark.trim(),
                });
            }

            handleClose();
            Swal.fire({
                title: "Success",
                text: "Reminder created successfully",
                icon: "success",
                showConfirmButton: false,
                timer: 2000,
            });
            fetchData();
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage("An error occurred while creating the reminder. Please try again later.");
            }
        }
    };

    return (
        <Dialog
            maxWidth="lg"
            fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">Create Reminder</DialogTitle>
            {errorMessage && (
                <Alert variant="outlined" severity="error" style={{ marginLeft: "24px", marginRight: "24px" }}>
                    {errorMessage}
                </Alert>
            )}
            <DialogContent style={{ paddingBottom: "0px" }}>
                <Divider style={{ paddingTop: "0px", paddingBottom: "4px" }}>
                    <Chip label="Touchpoint Details" />
                </Divider>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField margin="dense" label="Organization" fullWidth value={newData.org_name} InputProps={{ readOnly: true }} sx={{ background: "#e7e7e79e" }} />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <TextField margin="dense" label="Email" fullWidth value={newData.email} InputProps={{ readOnly: true }} sx={{ background: "#e7e7e79e" }} />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField margin="dense" label="Office Phone" fullWidth value={newData.office_phone} InputProps={{ readOnly: true }} sx={{ background: "#e7e7e79e" }} />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField margin="dense" label="Mobile Phone" fullWidth value={newData.mobile_phone} InputProps={{ readOnly: true }} sx={{ background: "#e7e7e79e" }} />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField margin="dense" label="PIC Name" fullWidth value={newData.pic_name} InputProps={{ readOnly: true }} sx={{ background: "#e7e7e79e" }} />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <Autocomplete
                            options={productsData}
                            groupBy={(option) => {
                                const country = Countries.find((country) => country.id === option.country_id);
                                return country ? country.name : "Unknown";
                            }}
                            getOptionLabel={(option) => option.product_description}
                            onChange={(event, value) => {
                                setSelectedProductValue(value);
                                setNewData({ ...newData, product_id: value ? value.product_id : "" });
                            }}
                            isOptionEqualToValue={(option, value) => option.product_id === value.product_id}
                            value={selectedProductValue}
                            renderOption={(props, option) => {
                                return (
                                    <li {...props} key={option.product_id}>
                                        {option.product_description}
                                    </li>
                                );
                            }}
                            renderInput={(params) => <TextField {...params} margin="dense" id="product_id" label="Product" fullWidth />}
                            renderGroup={(params) => {
                                return (
                                    <li key={params.key}>
                                        <GroupHeader>{params.group}</GroupHeader>
                                        <GroupItems>{params.children}</GroupItems>
                                    </li>
                                );
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            options={usersData}
                            getOptionLabel={(option) => (option ? option.name : "Select user or group")}
                            groupBy={(option) => option.is_group}
                            onChange={(event, value) => {
                                setSelectedUserValue(value);
                                setNewData({ ...newData, assigned_to: value ? value.id : "", is_assigned_to_group: value ? value.is_group : 0 });
                                validate("assigned_to", value ? value.id : "");
                            }}
                            isOptionEqualToValue={(option, value) => option.id === value.id && option.is_group === value.is_group}
                            value={selectedUserValue}
                            renderInput={(params) => <TextField {...params} margin="dense" id="assigned_to" label="Assigned To" fullWidth required error={errors.assigned_to} helperText={helperText.assigned_to} />}
                            renderGroup={(params) => (
                                <li key={params.key}>
                                    <GroupHeader>{params.group === 1 ? "Groups" : "Users"}</GroupHeader>
                                    <GroupItems>{params.children}</GroupItems>
                                </li>
                            )}
                        />
                    </Grid>
                </Grid>
                <TextField
                    autoComplete="off"
                    margin="dense"
                    label="Remark"
                    type="text"
                    multiline
                    rows={4}
                    fullWidth
                    required
                    value={newData.remark}
                    error={errors.remark}
                    helperText={helperText.remark}
                    onChange={(e) => {
                        setNewData({ ...newData, remark: e.target.value });
                        validate("remark", e.target.value);
                    }}
                />
                <Grid container spacing={2} sx={{ pt: 1 }}>
                    <Grid item xs={12} style={{ textAlign: "center" }}>
                        <Button variant="contained" style={{ backgroundColor: "#1e73dd", color: "white" }} onClick={handleAddReminder}>
                            Add Reminder
                        </Button>
                    </Grid>
                </Grid>
                {errors.reminders && helperText.reminders && (
                    <Alert variant="outlined" severity="error" style={{ marginTop: "10px" }}>
                        {helperText.reminders}
                    </Alert>
                )}
                {reminderRows.map((row, index) => (
                    <Grid item container spacing={2} key={index} sx={{ pt: 0, mt: 1.5, pb: 1.5, border: "1px solid #c4c4c4" }}>
                        <Grid item xs={11}>
                            <Grid container spacing={2} sx={{ p: 1 }}>
                                <Grid item xs={12} sm={5}>
                                    <TextField
                                        margin="dense"
                                        label="Reminder Schedule"
                                        select
                                        fullWidth
                                        value={row.schedule_month}
                                        onChange={(event) => handleReminderMonthChange(index, event.target.value)}
                                    >
                                        {REMINDER_MONTH_OPTIONS.map((monthOption) => (
                                            <MenuItem key={monthOption} value={monthOption}>
                                                {monthOption} Month{monthOption > 1 ? "s" : ""}
                                            </MenuItem>
                                        ))}
                                        <MenuItem value={CUSTOM_SCHEDULE_VALUE}>Custom</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={7}>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DatePicker
                                            label="Reminder Date"
                                            inputFormat="YYYY-MM-DD"
                                            value={row.reminder_date}
                                            onChange={(newValue) => {
                                                handleReminderDateChange(index, newValue);
                                            }}
                                            renderInput={(params) => <TextField {...params} required fullWidth margin="dense" error={Boolean(reminderErrors[index])} helperText={reminderErrors[index] ? "Reminder Date is required." : ""} />}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={1}>
                            <Button variant="contained" color="secondary" onClick={() => handleRemoveReminder(index)}>
                                X
                            </Button>
                        </Grid>
                    </Grid>
                ))}
            </DialogContent>
            <DialogActions style={{ margin: "12px" }}>
                <Button onClick={handleCreate} variant="contained" color="primary" style={{ marginRight: "6px", color: "white" }}>
                    Save
                </Button>
                <Button onClick={handleClose} variant="contained" color="error" style={{ color: "white" }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
